import {
    AccordionBody,
    Button,
    DropDown,
    toInt,
    ValidatedDropDownList,
    ValidatedDynamicList,
    ValidatedDynamicListFormComponentProps
} from '@finbeat/react-toolkit';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import BaseReactHookFormValue from 'base-components/BaseReactHookForm/BaseReactHookFormValue';
import {
    TariffPeriodParentServicesInfo,
    TariffPeriodServicesInfo,
    TariffsInfo
} from 'models/api/tariff';
import { useTariffInfoState } from 'providers/TariffInfoProvider';
import { useCallback, useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Accordion, AccordionHeader, AccordionItem, Col, Row } from 'reactstrap';
import { TariffAccesses } from './TariffsModal';
import { TariffsServiceForm, TariffsServiceFormWrapper } from './TariffsServiceForm';

type TariffsModalPeriodsServicesProps = TariffAccesses & {
    periodIdx: number;
    parentServiceList: DropDown[];
    parentServiceListLoading: boolean;
};

const TariffsModalPeriodsServices = ({
    periodIdx,
    parentServiceList,
    parentServiceListLoading,
    fullAccess,
}: TariffsModalPeriodsServicesProps) => {
    const { t } = useTranslation();
    const { setFocus, watch } = useFormContext<TariffsInfo>();
    const { fields, append, remove } = useFieldArray<TariffsInfo>({
        name: `periods.${periodIdx}.parentServices`,
    });
    const [open, setOpen] = useState('');

    const accordionProps = {
        open: open,
        toggle: (id: string) => setOpen((prev) => (prev === id ? '' : id)),
    };

    const emptyService: TariffPeriodParentServicesInfo = useMemo(
        () => ({
            id: 0,
            parentServiceId:
                typeof parentServiceList[0]?.id === 'number' ? parentServiceList[0]?.id : 0,
            parentServiceName: '',
            type: 'formula',
            periodServiceDefId: null,
            services: [],
        }),
        [parentServiceList]
    );

    const addService = () => {
        append(emptyService, { shouldFocus: true });
        setTimeout(
            () => setFocus(`periods.${periodIdx}.parentServices.${fields.length}.parentServiceId`),
            1
        );
    };

    const deleteService = (idx: number) => {
        remove(idx);
    };

    const parentServices = watch(`periods.${periodIdx}.parentServices`);
    const checkDoublesParentServices = (value: number) => {
        if (parentServices.filter((x) => x.parentServiceId === value).length > 1)
            return 'BackOfficeErrorDuplicatedValue';
    };

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <span>{t('BackOfficeServices')}</span>
                <div>
                    <Button outline disabled={!fullAccess} onClick={addService}>
                        <Trans i18nKey="BackOfficeAdd" />
                    </Button>
                </div>
            </div>
            <Accordion {...accordionProps}>
                {fields.map(({ id }, idx) => (
                    <AccordionItem key={id}>
                        <AccordionHeader tag="div" targetId={id}>
                            <div>
                                <BaseReactHookFormValue<TariffsInfo>
                                    name={`periods.${periodIdx}.parentServices.${idx}.parentServiceId`}
                                    renderCallBack={(value) =>
                                        parentServiceList.find((x) => x.id === value)?.name ?? ''
                                    }
                                />

                                {fullAccess && (
                                    <FontAwesomeIcon
                                        icon={faTrashCan}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            deleteService(idx);
                                        }}
                                        className="text-danger"
                                    />
                                )}
                            </div>
                        </AccordionHeader>

                        <AccordionBody
                            accordionId={id}
                            toggle={(val) => val && setOpen(val.toString())}
                        >
                            <Row>
                                <Col>
                                    <ValidatedDropDownList<TariffsInfo>
                                        name={`periods.${periodIdx}.parentServices.${idx}.parentServiceId`}
                                        label={t('BackOfficeParentService')}
                                        items={parentServiceList}
                                        isLoading={parentServiceListLoading}
                                        isClearable={false}
                                        rules={{
                                            validate: checkDoublesParentServices,
                                        }}
                                        disabled={!fullAccess}
                                    />
                                </Col>
                            </Row>
                            <Services
                                periodIdx={periodIdx}
                                parentServiceIdx={idx}
                                fullAccess={fullAccess}
                            />
                        </AccordionBody>
                    </AccordionItem>
                ))}
            </Accordion>
        </>
    );
};

const Services = ({
    periodIdx,
    parentServiceIdx,
    fullAccess,
}: TariffAccesses & {
    periodIdx: number;
    parentServiceIdx: number;
}) => {
    const { dicts, service } = useTariffInfoState();
    const FormComponent = useCallback(
        (props: ValidatedDynamicListFormComponentProps) => (
            <TariffsServiceForm
                {...props}
                periodIdx={periodIdx}
                parentServiceIdx={parentServiceIdx}
            />
        ),
        []
    );

    const emptyRecord = useMemo<TariffPeriodServicesInfo>(
        () => ({
            id: null,
            serviceId: toInt(service[0]?.id) ?? 0,
            formula: '',
            serviceType: dicts.serviceTypes[0]?.id?.toString() ?? '',
            calcType: dicts.calcTypes[0]?.id?.toString() ?? '',
            idx: null,
            roleName: '',
            isEditable: false,
        }),
        [service, dicts]
    );

    return (
        <ValidatedDynamicList<TariffsInfo, TariffPeriodServicesInfo>
            formComponent={FormComponent}
            wrapper={TariffsServiceFormWrapper}
            emptyRecord={emptyRecord}
            name={`periods.${periodIdx}.parentServices.${parentServiceIdx}.services`}
            disabled={!fullAccess}
        />
    );
};

export default TariffsModalPeriodsServices;
